import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSSE } from "react-hooks-sse";

import { ApiJob, ApiJobNote, isJobEvent, JobEvent } from "@common/api/v1.1/types/jobs";
import { ApiUser } from "@common/api/v1.1/types/users";
import { Actions } from "../../types";
import { AddNote } from "./AddNote";
import { EditNote } from "./EditNote";
import { NoteContent } from "./NoteContent";

import { Spinner } from "reactstrap";
import "./styles.scss";

type Props = Readonly<{
  addNote: Actions["addNote"];
  deleteNote: Actions["deleteNote"];
  fetchNotes: Actions["fetchNotes"];
  job: ApiJob;
  onUpdateNoteCount: (count: number) => void;
  updateNote: Actions["updateNote"];
  user: ApiUser;
}>;

export const Notes = ({
  addNote,
  deleteNote,
  fetchNotes,
  job,
  onUpdateNoteCount,
  updateNote,
  user,
}: Props) => {
  const [notes, setNotes] = useState<Array<ApiJobNote> | undefined>(undefined);
  const [editingNotes, setEditingNotes] = useState<{ [noteId: string]: true }>({});
  const refreshNotes = useCallback(async () => {
    try {
      const fetchedNotes = await fetchNotes(job.id);
      setNotes(fetchedNotes);
      onUpdateNoteCount(fetchedNotes.length);
    } catch (err) {
      console.error(err);
      setNotes([]);
    }
  }, [job.id, onUpdateNoteCount, setNotes]);
  useEffect(() => {
    setNotes(undefined);
    setEditingNotes({});
  }, [job.id]);
  const notesWrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    (async () => {
      if (notes !== undefined) {
        if (notesWrapperRef.current) {
          notesWrapperRef.current.scrollTop = notesWrapperRef.current.scrollHeight;
        }
        return;
      }
      refreshNotes();
    })();
  }, [notes]);
  useSSE(
    "jobEvent",
    {},
    {
      parser: input => JSON.parse(input),
      stateReducer: (state, action) => {
        if (action.data) {
          const parsedData: any = action.data;
          if (
            typeof parsedData.job === "object" &&
            parsedData.job !== null &&
            !Array.isArray(parsedData.job) &&
            isJobEvent(parsedData.type)
          ) {
            if (parsedData.job.id === job.id && parsedData.type === JobEvent.JobNotesChanged) {
              refreshNotes();
            }
            return state;
          }
        }
        return state;
      },
    },
  );
  const onDeleteNote = useCallback(
    async (noteId: string) => {
      await deleteNote(job.id, noteId);
      await refreshNotes();
    },
    [job.id],
  );
  const onEditNote = useCallback(
    (noteId: string) => {
      setEditingNotes(n => ({
        ...n,
        [noteId]: true,
      }));
    },
    [job.id],
  );
  const cancelEdit = useCallback(
    (noteId: string) => {
      setEditingNotes(n => {
        if (!(noteId in n)) {
          return n;
        }
        const updated = { ...n };
        delete updated[noteId];
        return updated;
      });
    },
    [job.id],
  );
  const saveEdit = useCallback(
    (noteId: string, content: string) => {
      updateNote(job.id, noteId, content).then(() => {
        refreshNotes().then(() => {
          setEditingNotes(n => {
            const updated = { ...n };
            delete updated[noteId];
            return updated;
          });
        });
      });
    },
    [job.id],
  );
  let contents = null;
  if (notes === undefined) {
    contents = (
      <div className="text-center text-white">
        <Spinner size="sm" />
      </div>
    );
  } else if (Array.isArray(notes)) {
    if (notes.length < 1) {
      contents = <div className="text-white">No notes found.</div>;
    } else {
      contents = (
        <>
          {notes.map(n => (
            <div className="note" key={n.id}>
              {n.id in editingNotes ? (
                <EditNote
                  cancelEdit={cancelEdit}
                  initialContent={n.content}
                  noteId={n.id}
                  saveEdit={saveEdit}
                />
              ) : (
                <NoteContent
                  canDelete={n.owner.id === user.id || job.owner.id === user.id}
                  canEdit={n.owner.id === user.id}
                  onDelete={onDeleteNote}
                  onEdit={onEditNote}
                  note={n}
                  user={user}
                />
              )}
            </div>
          ))}
        </>
      );
    }
  }
  return (
    <div className="notes">
      <div className="note-list-wrapper" ref={notesWrapperRef}>
        {contents}
      </div>
      {Array.isArray(notes) && (
        <AddNote addNote={addNote} jobId={job.id} refreshNotes={refreshNotes} />
      )}
    </div>
  );
};
Notes.displayName = "Notes";
